// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material'

export default function Login({ onLoginSuccess }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('')
  const [forgotPasswordError, setForgotPasswordError] = useState('')
  const [isSendingReset, setIsSendingReset] = useState(false)
  const [providers, setProviders] = useState({ microsoft: true, google: true, email: true })

  // Get branding from config
  const branding = window.INSIGHT_CONFIG?.BRANDING || {}
  const appName = window.INSIGHT_CONFIG?.APP_NAME || 'Insight'
  const tagline = branding.tagline || 'AI assistant'

  // Fetch enabled auth providers (auth.providers in config.yaml) — fail-open
  // to all enabled so a fetch error never hides the login buttons.
  useEffect(() => {
    fetch('/auth/providers')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data) setProviders(data) })
      .catch(() => {})
  }, [])

  // Check if already logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('cme_token')
    const userStr = localStorage.getItem('cme_user')
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr)
        if (user.role === 'super_admin') {
          window.location.href = 'super-admin.html'
        } else {
          onLoginSuccess()
        }
      } catch (e) {
        // If parsing fails, proceed with login
      }
    }
  }, [onLoginSuccess])

  // Check for OAuth error in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const oauthError = params.get('oauth_error')
    if (oauthError) {
      setError(oauthError)
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || data.error || 'Login failed')
      }

      // Save token to localStorage
      localStorage.setItem('cme_token', data.access_token)

      // Redirect super_admin immediately (they don't need full profile here)
      if (data.user.role === 'super_admin') {
        localStorage.setItem('cme_user', JSON.stringify(data.user))
        window.location.href = 'super-admin.html'
        return
      }

      // Fetch full user profile (includes theme, last_selected_gpt, etc.)
      try {
        const meResponse = await fetch('/users/me', {
          headers: { Authorization: `Bearer ${data.access_token}` }
        })
        if (meResponse.ok) {
          const fullUser = await meResponse.json()
          localStorage.setItem('cme_user', JSON.stringify(fullUser))
        } else {
          // Fallback to basic user data from login response
          localStorage.setItem('cme_user', JSON.stringify(data.user))
        }
      } catch {
        // Fallback to basic user data from login response
        localStorage.setItem('cme_user', JSON.stringify(data.user))
      }

      onLoginSuccess()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setForgotPasswordError('')
    setForgotPasswordMessage('')
    setIsSendingReset(true)

    try {
      const response = await fetch('/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset email')
      }

      setForgotPasswordMessage(data.message)
    } catch (err) {
      setForgotPasswordError(err.message)
    } finally {
      setIsSendingReset(false)
    }
  }

  const handleBackToLogin = () => {
    setShowForgotPassword(false)
    setForgotPasswordEmail('')
    setForgotPasswordMessage('')
    setForgotPasswordError('')
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 520, borderRadius: 4, boxShadow: 6 }}>
        <CardContent sx={{ p: 5 }}>
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom>
              {appName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {tagline}
            </Typography>
          </Box>

          {showForgotPassword ? (
            /* Forgot Password Form */
            <Box component="form" onSubmit={handleForgotPassword}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                margin="normal"
                required
                autoFocus
                placeholder="Enter your email"
              />

              {forgotPasswordError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {forgotPasswordError}
                </Alert>
              )}

              {forgotPasswordMessage && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {forgotPasswordMessage}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSendingReset || !!forgotPasswordMessage}
                sx={{ mt: 3, py: 1.5, fontWeight: 600 }}
              >
                {isSendingReset ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleBackToLogin}
                  sx={{ cursor: 'pointer' }}
                >
                  Back to Login
                </Link>
              </Box>
            </Box>
          ) : (
            /* Login Form */
            <>
              {/* OAuth Buttons — first, the fastest way to log in */}
              {providers.microsoft && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => { window.location.href = `/auth/microsoft/login?frontend_base=${encodeURIComponent(window.location.origin)}` }}
                  sx={{ mb: 1, py: 1.5, textTransform: 'none', fontWeight: 500, borderColor: 'divider', color: 'text.primary', fontSize: '1rem' }}
                  startIcon={
                    <svg width="20" height="20" viewBox="0 0 23 23">
                      <path fill="#f35325" d="M1 1h10v10H1z"/>
                      <path fill="#81bc06" d="M12 1h10v10H12z"/>
                      <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                      <path fill="#ffba08" d="M12 12h10v10H12z"/>
                    </svg>
                  }
                >
                  Sign in with Microsoft
                </Button>
              )}

              {providers.google && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => { window.location.href = `/auth/google/login?frontend_base=${encodeURIComponent(window.location.origin)}` }}
                  sx={{ py: 1.2, textTransform: 'none', fontWeight: 500, borderColor: 'divider', color: 'text.primary' }}
                  startIcon={
                    <svg width="20" height="20" viewBox="0 0 48 48">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                  }
                >
                  Sign in with Google
                </Button>
              )}

              {/* Divider — only when both OAuth and email are available */}
              {(providers.microsoft || providers.google) && providers.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                    or
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Email/Password Form */}
              {providers.email && (
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                  placeholder="Enter your email"
                />

                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  placeholder="Enter your password"
                />

                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setShowForgotPassword(true)}
                    sx={{ cursor: 'pointer' }}
                  >
                    Forgot password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading}
                  sx={{ mt: 2, py: 1.5, fontWeight: 600 }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Login'
                  )}
                </Button>
              </Box>
              )}

              {/* Info about invitations */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  New to {appName}? Contact your company administrator for an invitation.
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
